import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import './index.scss';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectRiaProfile } from '@/store/features/userinfo/userinfoSlice';
import {
  selectApexAccounts,
  selectApexLoading,
  selectApexState,
} from '@/store/features/apex/apex-selectors';
import { APEX_STALE_USER_REQUEST, fetchApexAccounts } from '@/store/features/apex/apex-thunks';
import { buildAgreementRows, STATUS_LABELS } from './agreement-rows';

const Agreements = () => {
  const dispatch = useAppDispatch();
  const riaProfile = useAppSelector(selectRiaProfile);
  const apexAccounts = useAppSelector(selectApexAccounts);
  const apexLoading = useAppSelector(selectApexLoading);
  const apexError = useAppSelector(selectApexState).error;
  const [apexLoadFailed, setApexLoadFailed] = useState(false);
  const apexEnabled =
    Boolean(riaProfile.profile?.apex_correspondent_id) &&
    Boolean(riaProfile.features?.apex_enabled);
  // An account whose detail request failed is listed without its agreements.
  const hasAccountWithoutDetail = apexAccounts.some((account) => !account.account_detail);

  const loadApexAccounts = useCallback(() => {
    setApexLoadFailed(false);
    dispatch(fetchApexAccounts())
      .unwrap()
      .catch((error: unknown) => {
        const aborted = (error as { name?: string } | null)?.name === 'AbortError';
        if (!aborted && error !== APEX_STALE_USER_REQUEST) {
          setApexLoadFailed(true);
        }
      });
  }, [dispatch]);

  // Apex accounts are only in the store after Wealth or Investor Profile has
  // loaded them. Wait out a request already in flight (one Wealth started may
  // be cancelled when Wealth unmounts), then load once per visit if accounts
  // are still missing or incomplete. Later failures are left to the Retry
  // action, so this never loops.
  const attemptedLoadRef = useRef(false);
  const needsApexAccounts = apexEnabled && (apexAccounts.length === 0 || hasAccountWithoutDetail);
  useEffect(() => {
    if (needsApexAccounts && !apexLoading.accounts && !attemptedLoadRef.current) {
      attemptedLoadRef.current = true;
      loadApexAccounts();
    }
  }, [needsApexAccounts, apexLoading.accounts, loadApexAccounts]);

  const showApexLoadError =
    apexEnabled &&
    !apexLoading.accounts &&
    (apexLoadFailed ||
      hasAccountWithoutDetail ||
      (apexAccounts.length === 0 && Boolean(apexError)));

  const rows = buildAgreementRows(riaProfile.enabled_agreements ?? [], apexAccounts);

  return (
    <div className="record-agreements">
      {showApexLoadError && (
        <Alert
          className="record-agreements-error"
          type="warning"
          showIcon
          message="Some account agreements couldn't be loaded."
          action={
            <Button size="small" onClick={loadApexAccounts}>
              Retry
            </Button>
          }
        />
      )}
      <section className="record-agreements-card" aria-label="Agreements">
        {apexLoading.accounts && rows.length === 0 ? (
          <div className="record-agreements-loading">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <p className="record-agreements-empty">No agreements yet.</p>
        ) : (
          <table className="record-agreements-table">
            <thead>
              <tr>
                <th scope="col">Document name</th>
                <th scope="col">Details</th>
                <th scope="col">Version</th>
                <th scope="col">Status</th>
                <th scope="col">Date signed</th>
                <th scope="col">Signed method</th>
                <th scope="col">
                  <span className="record-agreements-sr-only">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="record-agreements-name" data-label="Document name">
                    {row.name}
                  </td>
                  <td className="record-agreements-details" data-label="Details">
                    {row.details}
                  </td>
                  <td className="record-agreements-mono" data-label="Version">
                    {row.version ?? '—'}
                  </td>
                  <td data-label="Status">
                    <span className={`record-agreements-status is-${row.status}`}>
                      {row.status !== 'unknown' && <i aria-hidden="true" />}
                      {STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="record-agreements-mono" data-label="Date signed">
                    {row.signedOn ?? '—'}
                  </td>
                  <td data-label="Signed method">{row.status === 'signed' ? 'Electronic' : '—'}</td>
                  <td className="record-agreements-action">
                    {row.url && (
                      <a
                        className="record-agreements-view"
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${row.name}`}
                      >
                        <LinkOutlined aria-hidden="true" />
                        View
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <p className="record-agreements-note">
        Agreements you sign when opening accounts appear here. Unsigned agreements are finished in
        the account opening flow.
      </p>
    </div>
  );
};

export default Agreements;
